"use client";

import * as React from "react";
import { useId } from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { AnimatePresence, motion } from "framer-motion";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const tabsListVariants = cva(
  "inline-flex w-fit items-center justify-center text-contrast/70",
  {
    variants: {
      variant: {
        default: "bg-contrast/3",
        search: "bg-contrast/3",
        ghost: "bg-transparent",
        "ghost-muted": "bg-transparent",
        inset: "bg-transparent",
      },
      size: {
        sm: "h-7.5 rounded-md gap-0.5 p-0.5",
        default: "h-8 rounded-md gap-0.5 p-0.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const tabsTriggerVariants = cva(
  "relative cursor-pointer inline-flex flex-1 items-center justify-center whitespace-nowrap transition-colors disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      size: {
        sm: "h-full gap-1 rounded-[0.45rem] px-2.5 text-xs font-medium [&_svg:not([class*='size-'])]:size-4",
        default:
          "h-full gap-1.5 rounded-md px-3 text-sm font-medium [&_svg:not([class*='size-'])]:size-4",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

const tabsIndicatorVariants = cva(
  "absolute inset-0 block overflow-hidden border",
  {
    variants: {
      variant: {
        default:
          "bg-gradient-to-t border-black/10 from-black/8 to-black/3 dark:border-white/10 dark:from-white/8 dark:to-white/3",
        ghost:
          "bg-gradient-to-t border-black/8 from-black/6 to-black/[0.02] dark:border-white/8 dark:from-white/6 dark:to-white/[0.02]",
        "ghost-muted":
          "border-transparent bg-muted text-muted-foreground shadow-[0px_1px_1px_rgba(0,0,0,0.04)]",
        inset:
          "rounded-md border border-border bg-surface-primary shadow-sm dark:border-white/10 dark:bg-surface-elevated dark:shadow-none",
        search: "border-[var(--tab-active-border)] bg-[var(--tab-active-bg)]",
      },
      size: {
        sm: "rounded-[0.45rem]",
        default: "rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type TabsVariant = NonNullable<
  VariantProps<typeof tabsListVariants>["variant"]
>;
type TabsSize = NonNullable<VariantProps<typeof tabsListVariants>["size"]>;

const TabsContext = React.createContext<{
  layoutId: string;
  value?: string;
  variant: TabsVariant;
  size: TabsSize;
} | null>(null);

interface TabsProps
  extends
    React.ComponentProps<typeof TabsPrimitive.Root>,
    VariantProps<typeof tabsListVariants> {}

function Tabs({
  value: valueProp,
  defaultValue,
  onValueChange,
  variant = "default",
  size = "default",
  ...props
}: TabsProps) {
  const generatedId = useId();
  const layoutId = `tabs-indicator-${generatedId}`;

  return (
    <TabsContext.Provider
      value={{
        layoutId,
        value: valueProp,
        variant: variant ?? "default",
        size: size ?? "default",
      }}
    >
      <TabsPrimitive.Root
        data-slot="tabs"
        value={valueProp}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        {...props}
      />
    </TabsContext.Provider>
  );
}

function TabsList({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  const context = React.useContext(TabsContext);
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        tabsListVariants({
          variant: context?.variant,
          size: context?.size,
        }),
        className,
      )}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  value,
  children,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const context = React.useContext(TabsContext);
  const isActive = context?.value === value;

  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      value={value}
      className={cn(
        tabsTriggerVariants({ size: context?.size }),
        !isActive && "rounded-md hover:bg-contrast/[0.04]",
        className,
      )}
      {...props}
    >
      {isActive && context && (
        <motion.span
          layoutId={context.layoutId}
          className={tabsIndicatorVariants({
            variant: context.variant,
            size: context.size,
          })}
          transition={{
            type: "spring",
            damping: 30,
            stiffness: 500,
            mass: 0.5,
          }}
        />
      )}
      <span
        className={cn(
          "relative z-10 inline-flex items-center gap-1.5 transition-colors",
          isActive ? "text-heading" : "text-contrast/70",
        )}
      >
        {children}
      </span>
    </TabsPrimitive.Trigger>
  );
}

function TabsContent({
  className,
  children,
  value,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  const context = React.useContext(TabsContext);
  const isActive = context?.value === value;

  return (
    <AnimatePresence mode="popLayout" initial={false}>
      {isActive && (
        <TabsPrimitive.Content
          data-slot="tabs-content"
          className={cn("flex-1 outline-none", className)}
          forceMount
          value={value}
          asChild
          {...props}
        >
          <motion.div
            key={value}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
              mass: 0.8,
            }}
          >
            {children}
          </motion.div>
        </TabsPrimitive.Content>
      )}
    </AnimatePresence>
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
export type { TabsVariant, TabsSize };
